/* =============================================================
   MBB FREE TOOL — APP LOGIC (v2, 2026-09-23)
   Single-file vanilla JS. No build step.
   Brand rules: no em dashes, no "and" in styled type (use &),
   no italic Fraunces, validate-before-educate voice.

   v2 replaces the swipe game + sample day with:
     - recipe cards ported from the app's recipe view (photo, macros,
       "make it yours" swaps + toggles that update the macros live)
     - a mock day builder ported from the app's day planner (drag or
       tap a card into a day, totals add up against her numbers)
   Recipe data lives in recipes-data.js (generated from the live app
   by scripts/export-recipes.mjs so macros stay identical to the app).
   ============================================================= */

/* -------------------------------------------------------------
   KLAVIYO CONFIG
   publicKey is the 6-char "Public API Key / company_id" from
   Klaviyo Settings → API Keys. It is safe to expose client-side
   (read-only would-be access; can only add subscribers).
   ------------------------------------------------------------- */
const KLAVIYO = {
  publicKey: 'Utm2H6',
  listId: 'WuVKCE',
};

// Resolves a recipe img path to either the local file (for dev) or the
// Shopify CDN asset URL (for production). The Liquid template injects
// window.FREE_TOOL_ASSETS = { r1: '<asset_url>', ... } on production.
function resolveImg(path) {
  if (typeof window !== 'undefined' && window.FREE_TOOL_ASSETS) {
    const id = path.split('/').pop().replace(/\.jpg$/, '');
    return window.FREE_TOOL_ASSETS[id] || path;
  }
  return path;
}

async function pushToKlaviyo(email, bio, macros) {
  if (!KLAVIYO.publicKey || KLAVIYO.publicKey === 'YOUR_PUBLIC_KEY_HERE') {
    console.warn('[klaviyo] public key not set, skipping');
    return { ok: false, skipped: true };
  }
  const goalLabel = ({
    'fat-loss': 'fat loss', 'muscle-gain': 'muscle gain', 'recomp': 'recomp', 'maintain': 'maintain',
  })[bio.goal] || bio.goal;
  const body = {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: 'free-tool',
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email: email,
              properties: {
                source: 'free-tool',
                goal: goalLabel,
                activity_multiplier: bio.activity,
                weight_kg: bio.weight,
                height_cm: bio.height,
                age: bio.age,
                bmi: macros?.bmi ?? null,
                protein_target_g: macros?.protein ?? null,
                calorie_target: macros?.cals ?? null,
                fat_target_g: macros?.fat ?? null,
                carbs_target_g: macros?.carbs ?? null,
                tdee: macros?.tdee ?? null,
                bmr: macros?.bmr ?? null,
                guardrails_triggered: (macros?.guards || []).map(g => g.tag).join(', ') || 'none',
                blocked_reason: macros?.blocked || null,
                captured_at: new Date().toISOString(),
              },
            },
          },
        },
      },
      relationships: {
        list: { data: { type: 'list', id: KLAVIYO.listId } },
      },
    },
  };
  try {
    const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO.publicKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2024-10-15',
      },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      console.log('[klaviyo] subscribed', email, 'status', res.status);
      return { ok: true, status: res.status };
    }
    const text = await res.text().catch(() => '');
    console.error('[klaviyo] failed', res.status, text);
    return { ok: false, status: res.status, error: text };
  } catch (e) {
    console.error('[klaviyo] network error', e);
    return { ok: false, error: e.message };
  }
}

/* -------------------------------------------------------------
   MARQUEE PHRASES
   ------------------------------------------------------------- */
const MARQUEE_PHRASES = [
  'not another diet.',
  'education-backed freedom',
  'your real tuesday, not someone else\'s',
  'you don\'t have a discipline problem',
  'where you start, not the answer',
  'stop fighting yourself',
];

/* -------------------------------------------------------------
   STATE
   ------------------------------------------------------------- */
const state = {
  current: 'screen-landing',
  history: ['screen-landing'],
  bio: {
    goal: null,        // fat-loss | muscle-gain | recomp | maintain
    activity: null,    // 1.3 | 1.5 | 1.7 | 1.9
    weight: null,      // kg, number
    height: null,      // cm, number
    age: null,         // years, number
  },
  email: null,
  macros: null,
  guardrails: [],
  // v2
  slot: 'breakfast',   // active card tab
  cardState: {},       // recipeId → { swap: [optionIdx per group], on: {toggleId: bool} }
  tray: [],            // meals she added: [{ uid, id, name, slot, img, cal, p, custom }]
  days: { mon: [], tue: [] }, // copies of tray meals placed into each mock day
  pickOpen: null,      // tray meal uid whose "add to" day picker is open
  dragging: false,
};

/* -------------------------------------------------------------
   SCREEN ROUTING
   ------------------------------------------------------------- */
function goTo(id, { pushHistory = true } = {}) {
  const next = document.getElementById(id);
  if (!next) return;
  document.querySelectorAll('.screen').forEach((s) => s.classList.remove('is-active'));
  next.classList.add('is-active');
  state.current = id;
  if (pushHistory) state.history.push(id);
  window.scrollTo({ top: 0, behavior: 'instant' });
  updateTopbar(id);
  updateDaybar();
}

function goBack(target) {
  // If target is provided, pop until we hit it.
  if (target) {
    while (state.history.length > 1 && state.history[state.history.length - 1] !== target) {
      state.history.pop();
    }
  } else {
    state.history.pop();
  }
  const prev = state.history[state.history.length - 1] || 'screen-landing';
  goTo(prev, { pushHistory: false });
}

function updateTopbar(id) {
  const meta = document.getElementById('topbar-meta');
  if (!meta) return;
  if (id === 'screen-results') meta.textContent = 'your personalised macros';
  else if (id === 'screen-email') meta.textContent = 'almost there';
  else if (id === 'screen-landing') meta.textContent = 'not another diet';
  else meta.textContent = 'free tool · 3 min';
}

/* -------------------------------------------------------------
   QUIZ INPUT HANDLERS
   ------------------------------------------------------------- */
function wireChoiceGroup(screenId) {
  const screen = document.getElementById(screenId);
  if (!screen) return;
  const choices = screen.querySelectorAll('.choice');
  const continueBtn = screen.querySelector('[data-next]');
  const field = screen.querySelector('.choices')?.dataset.field;

  choices.forEach((c) => {
    c.addEventListener('click', () => {
      choices.forEach((x) => x.classList.remove('is-selected'));
      c.classList.add('is-selected');
      const raw = c.dataset.value;
      state.bio[field] = field === 'activity' ? parseFloat(raw) : raw;
      continueBtn.disabled = false;
    });
  });

  if (continueBtn) {
    continueBtn.addEventListener('click', () => {
      const target = continueBtn.dataset.next;
      goTo(target);
    });
  }
}

function wireNumberField(screenId) {
  const screen = document.getElementById(screenId);
  if (!screen) return;
  const form = screen.querySelector('.numfield');
  const input = form.querySelector('input');
  const field = form.dataset.field;
  const continueBtn = screen.querySelector('[data-next]');

  const validate = () => {
    const v = parseFloat(input.value);
    if (!isFinite(v)) { continueBtn.disabled = true; return; }
    const min = parseFloat(input.min);
    const max = parseFloat(input.max);
    if (v < min || v > max) { continueBtn.disabled = true; return; }
    state.bio[field] = v;
    continueBtn.disabled = false;
  };

  input.addEventListener('input', validate);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !continueBtn.disabled) {
      e.preventDefault();
      continueBtn.click();
    }
  });

  continueBtn.addEventListener('click', () => {
    goTo(continueBtn.dataset.next);
  });
}

function wireBackButtons() {
  document.querySelectorAll('[data-back]').forEach((b) => {
    b.addEventListener('click', () => goBack(b.dataset.back));
  });
}

function wireLandingCTA() {
  document.querySelectorAll('[data-go]').forEach((b) => {
    b.addEventListener('click', () => goTo(b.dataset.go));
  });
}

/* -------------------------------------------------------------
   EMAIL GATE
   ------------------------------------------------------------- */
function wireEmailGate() {
  const input = document.getElementById('email-input');
  const submit = document.getElementById('email-submit');
  const err = document.getElementById('email-error');

  const tryGo = () => {
    const v = (input.value || '').trim().toLowerCase();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
    if (!valid) {
      err.classList.add('is-shown');
      return;
    }
    err.classList.remove('is-shown');
    state.email = v;

    // localStorage fallback so no lead is lost if Klaviyo is unreachable.
    try {
      localStorage.setItem('mbb_free_tool', JSON.stringify({
        email: v,
        bio: state.bio,
        capturedAt: new Date().toISOString(),
        source: 'free-tool',
      }));
    } catch (e) { /* localStorage disabled, fine */ }

    // Push to Klaviyo. Compute macros first so we send them as profile properties.
    // Fire-and-forget — we don't block UI on the network call.
    const macrosForKlaviyo = computeMacros(state.bio);
    pushToKlaviyo(v, state.bio, macrosForKlaviyo);

    // Meta Lead (warm, not a conversion). Defined in the Liquid template;
    // a no-op off the real domain and after the first call.
    if (window.mbbPixel) window.mbbPixel.lead(v);

    // Always continue to results (even on Klaviyo failure — lead is in localStorage).
    computeAndRenderResults();
  };

  submit.addEventListener('click', tryGo);
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') tryGo(); });
  input.addEventListener('input', () => err.classList.remove('is-shown'));
}

/* -------------------------------------------------------------
   MACRO FORMULA + GUARDRAILS
   ------------------------------------------------------------- */
function computeMacros(bio) {
  const { goal, activity, weight, height, age } = bio;

  // BMI early-check + age block — these route OUT of the macro flow.
  const bmi = weight / Math.pow(height / 100, 2);

  if (age < 18) {
    return { blocked: 'age', bmi };
  }
  if (bmi < 18.5 || bmi > 35) {
    return { blocked: 'bmi', bmi };
  }

  // Mifflin-St Jeor, female default.
  const bmr = (10 * weight) + (6.25 * height) - (5 * age) - 161;
  const tdee = bmr * activity;

  // Goal-adjusted calories
  const goalFactor = {
    'fat-loss': 0.85,
    'recomp': 0.95,
    'maintain': 1.00,
    'muscle-gain': 1.10,
  }[goal];

  const rawCals = tdee * goalFactor;

  // Protein (g)
  const proteinPerKg = {
    'fat-loss': 2.1,
    'muscle-gain': 1.8,
    'recomp': 1.9,
    'maintain': 1.9,
  }[goal];
  const rawProtein = weight * proteinPerKg;

  // Fat floor 0.8 g/kg
  const rawFat = weight * 0.8;

  // ---- APPLY GUARDRAILS ----
  const guards = [];
  let cals = rawCals;
  let protein = rawProtein;
  let deficitCap = 0.20; // default
  let surplusCap = 0.15;

  // #8 — age > 55 cap deficit at 15%
  if (age > 55 && (goal === 'fat-loss' || goal === 'recomp')) {
    deficitCap = 0.15;
    guards.push({
      tag: 'perimenopause-friendly',
      title: 'your deficit is held a touch gentler.',
      body: 'after 55, more aggressive deficits often backfire (lost lean mass, hormonal noise, hunger spiking). we keep your deficit at 15% maximum so the work actually holds.',
    });
  }

  // #2 — deficit > 20% (or > 15% if guardrail 8 fired)
  const deficitPct = (tdee - cals) / tdee;
  if (deficitPct > deficitCap) {
    const newCals = Math.round(tdee * (1 - deficitCap));
    guards.push({
      tag: 'deficit cap',
      title: 'we\'ve capped your deficit.',
      body: `a deficit deeper than ${Math.round(deficitCap * 100)}% of your maintenance is where the work tends to fall apart. you under-eat for a week, white-knuckle it, then the body course-corrects with a binge. cap protects the project.`,
      math: `math said ${Math.round(cals)} kcal. floor is ${newCals}.`,
    });
    cals = newCals;
  }

  // #3 — surplus > 15%
  const surplusPct = (cals - tdee) / tdee;
  if (surplusPct > surplusCap) {
    const newCals = Math.round(tdee * (1 + surplusCap));
    guards.push({
      tag: 'surplus cap',
      title: 'we\'ve capped your surplus.',
      body: 'past about a 15% surplus, the extra calories don\'t build more muscle. they just become fat. lean gaining wants patience, not a buffet.',
      math: `math said ${Math.round(cals)} kcal. ceiling is ${newCals}.`,
    });
    cals = newCals;
  }

  // #1 — kcal floor of 1500
  if (cals < 1500) {
    guards.push({
      tag: 'calorie floor',
      title: 'we\'ve set your floor to 1,500 kcal.',
      body: 'the math said something lower. anything below 1,500 starts to undermine the actual work. low energy, hormone disruption, lost muscle, and a body that fights you on the next plate of food. floor protects you.',
      math: `math said ${Math.round(cals)} kcal. floor is 1,500.`,
    });
    cals = 1500;
  }

  // #4 — protein < 100g
  if (protein < 100) {
    guards.push({
      tag: 'protein floor',
      title: 'protein floor set to 100g.',
      body: 'below 100g, you don\'t get the satiety, the muscle protection, or the metabolic upside protein is meant to bring. floor holds the lever in place.',
      math: `math said ${Math.round(protein)}g. floor is 100g.`,
    });
    protein = 100;
  }

  // #5 — protein > 180g
  if (protein > 180) {
    guards.push({
      tag: 'protein ceiling',
      title: 'protein capped at 180g.',
      body: 'past 180g, you\'re working harder than you need to. the returns flatten. you free up room in the rest of the plate for the other stuff that matters.',
      math: `math said ${Math.round(protein)}g. ceiling is 180g.`,
    });
    protein = 180;
  }

  // #9 — heavy training + fat-loss + bodyweight < 60kg
  if (activity === 1.9 && goal === 'fat-loss' && weight < 60) {
    guards.push({
      tag: 'soft flag',
      title: 'your numbers are on the edge of what\'s sustainable.',
      body: 'training that hard while eating in a deficit at your bodyweight is a small target. you\'re likely to lose strength, hair, cycle, sleep before you lose fat. worth a real conversation before you commit to this.',
    });
  }

  // Final calorie rounding + macro math
  cals = Math.round(cals);
  protein = Math.round(protein);
  let fat = Math.max(Math.round(rawFat), Math.round(cals * 0.20 / 9)); // never below 20% of cals
  // If fat × 9 + protein × 4 already exceeds cals, scale fat down to 20% minimum.
  if ((protein * 4) + (fat * 9) > cals * 0.95) {
    fat = Math.round(cals * 0.22 / 9);
  }
  const remainingCals = cals - (protein * 4) - (fat * 9);
  let carbs = Math.max(Math.round(remainingCals / 4), 60);

  return {
    blocked: null,
    bmi: Math.round(bmi * 10) / 10,
    bmr: Math.round(bmr),
    tdee: Math.round(tdee),
    cals,
    protein,
    fat,
    carbs,
    rawCals: Math.round(rawCals),
    rawProtein: Math.round(rawProtein),
    deficitPct: Math.round(deficitPct * 100),
    surplusPct: Math.round(surplusPct * 100),
    guards,
  };
}

/* -------------------------------------------------------------
   RENDER RESULTS
   ------------------------------------------------------------- */
function computeAndRenderResults() {
  const result = computeMacros(state.bio);

  if (result.blocked === 'age') {
    goTo('screen-block-age');
    return;
  }
  if (result.blocked === 'bmi') {
    goTo('screen-block-bmi');
    return;
  }

  state.macros = result;
  state.guardrails = result.guards;

  // Numbers
  document.getElementById('num-protein').textContent = result.protein;
  document.getElementById('num-cals').textContent = result.cals.toLocaleString();
  document.getElementById('cell-protein').textContent = result.protein;
  document.getElementById('cell-carbs').textContent = result.carbs;
  document.getElementById('cell-fat').textContent = result.fat;
  document.getElementById('cell-cals').textContent = result.cals.toLocaleString();
  document.getElementById('math-line').textContent =
    `BMR ${result.bmr} · TDEE ${result.tdee} · goal ${labelGoal(state.bio.goal)}.`;

  // Guardrails
  const host = document.getElementById('guardrails-host');
  host.innerHTML = '';
  if (result.guards.length === 0) {
    host.innerHTML = `<p class="muted mt-4" style="font-style:normal;">no guardrails triggered. your numbers sat cleanly inside the safe band.</p>`;
  } else {
    result.guards.forEach((g) => {
      const el = document.createElement('div');
      el.className = 'guardrail';
      el.innerHTML = `
        <span class="guardrail__tag">${g.tag}</span>
        <div class="guardrail__title">${g.title}</div>
        <p class="guardrail__body">${g.body}</p>
        ${g.math ? `<div class="guardrail__math">${g.math}</div>` : ''}
      `;
      host.appendChild(el);
    });
  }

  // Tomorrow's action — ~25% of daily protein, rounded to nearest 5
  const breakfastProtein = Math.round((result.protein * 0.25) / 5) * 5;
  document.getElementById('action-protein').textContent = breakfastProtein;

  // v2: cards + mock plan
  initCards();
  resetPlan();

  goTo('screen-results');
}

function labelGoal(goal) {
  return ({
    'fat-loss': 'fat loss · 15% deficit',
    'recomp': 'recomp · 5% deficit',
    'maintain': 'maintain · 0',
    'muscle-gain': 'muscle gain · 10% surplus',
  })[goal] || goal;
}

/* -------------------------------------------------------------
   RECIPE CARDS (ported from the app's recipe view)
   Macros = base + the delta of the chosen option in each swap group
   (option 0 is always the zero delta) + the delta of every toggle
   that is on. Nothing is recomputed from ingredients; this is
   exactly how food-recipes.js does it in the app.
   ------------------------------------------------------------- */
const SLOT_ORDER = ['breakfast', 'lunch', 'dinner', 'snack'];
const SLOT_PIP = { breakfast: 'b', lunch: 'l', dinner: 'd', snack: 's' };
const SLOT_LETTER = { breakfast: 'B', lunch: 'L', dinner: 'D', snack: 'S' };
const r1 = (x) => Math.max(0, Math.round(x * 10) / 10);
const stripTags = (s) => String(s || '').replace(/<[^>]+>/g, '');

function getCardState(recipe) {
  if (!state.cardState[recipe.id]) {
    state.cardState[recipe.id] = { swap: recipe.swapGroups.map(() => 0), on: {} };
  }
  return state.cardState[recipe.id];
}

function cardMacros(recipe, cs) {
  const d = { cal: 0, p: 0, f: 0, c: 0, fib: 0 };
  recipe.swapGroups.forEach((g, gi) => {
    const o = g.options[cs.swap[gi] || 0];
    if (o) { Object.keys(d).forEach((k) => { d[k] += o.d[k] || 0; }); }
  });
  recipe.toggles.forEach((t) => {
    if (cs.on[t.id]) { Object.keys(d).forEach((k) => { d[k] += t.d[k] || 0; }); }
  });
  const b = recipe.base;
  return {
    cal: Math.max(0, Math.round(b.cal + d.cal)),
    p: r1(b.p + d.p),
    c: r1(b.c + d.c),
    f: r1(b.f + d.f),
    fib: b.fib != null ? r1(b.fib + d.fib) : null,
  };
}

// "regular spaghetti · + second 95g can of tuna" — what she changed, for the day row.
function cardCustomText(recipe, cs) {
  const parts = [];
  recipe.swapGroups.forEach((g, gi) => {
    const idx = cs.swap[gi] || 0;
    if (idx > 0) parts.push(g.options[idx].label.toLowerCase());
  });
  recipe.toggles.forEach((t) => {
    if (cs.on[t.id]) parts.push(stripTags(t.label).toLowerCase());
  });
  return parts.join(' · ');
}

function initCards() {
  state.cardState = {};
  state.slot = 'breakfast';
  document.querySelectorAll('.slot-tab').forEach((b) => {
    b.classList.toggle('is-active', b.dataset.slot === state.slot);
  });
  renderCards();
}

function renderCards() {
  const grid = document.getElementById('card-grid');
  if (!grid) return;
  grid.innerHTML = '';
  RECIPES.filter((r) => (r.tab || r.slot) === state.slot).forEach((r) => grid.appendChild(makeCard(r)));
}

function makeCard(recipe) {
  const cs = getCardState(recipe);
  const el = document.createElement('article');
  el.className = 'ft-card';
  el.dataset.id = recipe.id;

  const tags = recipe.tags.slice();
  if (recipe.serves > 1) tags.push(`serves ${recipe.serves}`);

  const swapsHtml = recipe.swapGroups.map((g, gi) => `
    <div class="swap-q">${g.q}</div>
    <div class="swap-chips" data-group="${gi}">
      ${g.options.map((o, oi) => `<button type="button" class="swap-chip${(cs.swap[gi] || 0) === oi ? ' active' : ''}" data-opt="${oi}">${o.label}</button>`).join('')}
    </div>
    <div class="swap-note" data-note="${gi}">${g.options[cs.swap[gi] || 0]?.note || ''}</div>
  `).join('');

  const togglesHtml = recipe.toggles.map((t) => `
    <button type="button" class="swap-toggle${cs.on[t.id] ? ' on' : ''}" data-toggle="${t.id}">
      <span class="box">✓</span>
      <span class="txt">${t.label}</span>
      <span class="rd-tag" style="background:#fff;">${t.chip}</span>
    </button>
  `).join('');

  const hasSwitches = recipe.swapGroups.length || recipe.toggles.length;

  el.innerHTML = `
    <div class="rd-photo${recipe.fit === 'contain' ? ' contain' : ''}">
      <img src="${resolveImg(recipe.img)}" alt="" draggable="false" />
      <span class="dp-pip ${SLOT_PIP[recipe.slot]} rd-slotpip">${SLOT_LETTER[recipe.slot]}</span>
    </div>
    <div class="rd-photo-body">
      <div class="rd-name">${recipe.name}</div>
      <div class="rd-tagrow">${tags.map((t) => `<span class="rd-tag">${t}</span>`).join('')}</div>
    </div>
    <div class="rd-sec-label">macros per serve</div>
    <div class="rd-macros${recipe.base.fib == null ? ' four' : ''}">
      <div class="rd-mac" data-m="cal"><div class="v">·</div><div class="l">cal</div></div>
      <div class="rd-mac p" data-m="p"><div class="v">·</div><div class="l">protein</div></div>
      <div class="rd-mac" data-m="c"><div class="v">·</div><div class="l">carbs</div></div>
      <div class="rd-mac" data-m="f"><div class="v">·</div><div class="l">fat</div></div>
      ${recipe.base.fib != null ? '<div class="rd-mac" data-m="fib"><div class="v">·</div><div class="l">fibre</div></div>' : ''}
    </div>
    <div class="ft-card__actions">
      ${hasSwitches ? '<button type="button" class="ft-custom">customise</button>' : ''}
      <button type="button" class="ft-add">add to plan</button>
    </div>
    ${hasSwitches ? `
    <div class="ft-custom-panel" hidden>
      <div class="rd-sec-label">make it yours</div>
      <div class="swap-box">
        ${swapsHtml}
        ${togglesHtml}
      </div>
    </div>` : ''}
  `;

  // Customise: the switches stay folded until she asks (Britt, 2026-09-23: "photo, name,
  // macros, then button - customise, so its not so big on the screen").
  const customBtn = el.querySelector('.ft-custom');
  const panel = el.querySelector('.ft-custom-panel');
  const syncCustomBtn = () => {
    if (!customBtn) return;
    const changed = !!cardCustomText(recipe, cs);
    const open = !panel.hidden;
    customBtn.textContent = open ? 'done' : (changed ? 'customised ✓' : 'customise');
    customBtn.classList.toggle('is-open', open);
    customBtn.classList.toggle('has-changes', changed && !open);
  };
  if (customBtn) {
    customBtn.addEventListener('click', () => { panel.hidden = !panel.hidden; syncCustomBtn(); });
    el.addEventListener('click', (e) => { if (e.target.closest('.swap-chip, .swap-toggle')) syncCustomBtn(); });
    syncCustomBtn();
  }

  // Swap chips
  el.querySelectorAll('.swap-chips').forEach((chips) => {
    const gi = parseInt(chips.dataset.group, 10);
    chips.querySelectorAll('.swap-chip').forEach((chip) => {
      chip.addEventListener('click', () => {
        cs.swap[gi] = parseInt(chip.dataset.opt, 10);
        chips.querySelectorAll('.swap-chip').forEach((c) => c.classList.toggle('active', c === chip));
        const note = el.querySelector(`.swap-note[data-note="${gi}"]`);
        if (note) note.textContent = recipe.swapGroups[gi].options[cs.swap[gi]]?.note || '';
        updateCardMacros(recipe, el, true);
      });
    });
  });

  // Toggles
  el.querySelectorAll('.swap-toggle').forEach((t) => {
    t.addEventListener('click', () => {
      const id = t.dataset.toggle;
      cs.on[id] = !cs.on[id];
      t.classList.toggle('on', !!cs.on[id]);
      updateCardMacros(recipe, el, true);
    });
  });

  // Add
  el.querySelector('.ft-add').addEventListener('click', () => addToPlan(recipe));

  updateCardMacros(recipe, el, false);
  return el;
}

function updateCardMacros(recipe, el, bump) {
  const m = cardMacros(recipe, getCardState(recipe));
  const fmt = { cal: String(m.cal), p: `${m.p}g`, c: `${m.c}g`, f: `${m.f}g`, fib: m.fib != null ? `${m.fib}g` : '' };
  el.querySelectorAll('.rd-mac').forEach((cell) => {
    const k = cell.dataset.m;
    const v = cell.querySelector('.v');
    const next = fmt[k];
    if (v.textContent !== next) {
      v.textContent = next;
      if (bump) {
        cell.classList.remove('is-bump');
        void cell.offsetWidth; // restart the animation
        cell.classList.add('is-bump');
      }
    }
  });
}

function wireSlotTabs() {
  document.querySelectorAll('.slot-tab').forEach((b) => {
    b.addEventListener('click', () => {
      state.slot = b.dataset.slot;
      document.querySelectorAll('.slot-tab').forEach((x) => x.classList.toggle('is-active', x === b));
      renderCards();
    });
  });
}

/* -------------------------------------------------------------
   THE PLAN (ported from the app's day planner)
   A tray of the meals she added (switches baked in), and two mock
   days she drags them into. Dragging from the tray COPIES the meal
   into the day, exactly like the app's tray; tapping a tray row
   opens a day picker for touch.
   ------------------------------------------------------------- */
const DAYS = [{ id: 'mon', label: 'monday' }, { id: 'tue', label: 'tuesday' }];
const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

function targets() {
  return { cal: state.macros?.cals || 1700, p: state.macros?.protein || 125 };
}

function resetPlan() {
  state.tray = [];
  state.days = { mon: [], tue: [] };
  state.pickOpen = null;
  renderPlan();
}

function addToPlan(recipe) {
  const cs = getCardState(recipe);
  const m = cardMacros(recipe, cs);
  state.tray.push({
    uid: uid(), id: recipe.id, name: recipe.name, slot: recipe.slot, img: recipe.img,
    cal: m.cal, p: m.p, custom: cardCustomText(recipe, cs),
  });
  renderPlan();
  toast('added to your plan');
}

function placeInDay(meal, dayId) {
  if (!state.days[dayId]) return;
  state.days[dayId].push({ ...meal, uid: uid(), from: meal.uid });
  renderPlan();
  toast(`added to ${DAYS.find((d) => d.id === dayId)?.label || dayId}`);
}

function removeFromDay(dayId, u) {
  state.days[dayId] = state.days[dayId].filter((m) => m.uid !== u);
  renderPlan();
}

function removeFromTray(u) {
  state.tray = state.tray.filter((m) => m.uid !== u);
  if (state.pickOpen === u) state.pickOpen = null;
  renderPlan();
}

function dayTotals(dayId) {
  return state.days[dayId].reduce((a, d) => ({ cal: a.cal + d.cal, p: a.p + d.p }), { cal: 0, p: 0 });
}

function mealRowHtml(m, extra) {
  return `
    <span class="dp-pip ${SLOT_PIP[m.slot]}">${SLOT_LETTER[m.slot]}</span>
    <div class="dp-thumb"><img src="${resolveImg(m.img)}" alt="" draggable="false" /></div>
    <div class="dp-b">
      <div class="dp-n">${m.name}</div>
      ${m.custom ? `<div class="dp-c">${m.custom}</div>` : ''}
      <div class="dp-m"><strong>${m.p}g</strong> protein · ${m.cal} cal</div>
    </div>${extra || ''}`;
}

function renderPlan() {
  renderTray();
  DAYS.forEach(renderDayCard);
  updateDaybar();
}

let lastDragEnd = 0;

function renderTray() {
  const host = document.getElementById('tray-rows');
  if (!host) return;
  if (state.tray.length === 0) {
    host.innerHTML = `<div class="dp-empty">nothing here yet. tap <strong>add to plan</strong> on a recipe above.</div>`;
    return;
  }
  host.innerHTML = state.tray.map((m) => `
    <div class="dp-tray-row" data-uid="${m.uid}">
      ${mealRowHtml(m, `<button type="button" class="dp-x" aria-label="remove">✕</button><span class="dp-grab" title="drag into a day">⠿</span>`)}
    </div>
    <div class="dp-daypick${state.pickOpen === m.uid ? ' is-open' : ''}" data-for="${m.uid}">
      <span class="dp-daypick-lab">add to</span>
      ${DAYS.map((d) => `<button type="button" class="dp-dc" data-day="${d.id}">${d.label}</button>`).join('')}
    </div>`).join('');

  host.querySelectorAll('.dp-tray-row').forEach((row) => {
    const m = state.tray.find((x) => x.uid === row.dataset.uid);
    row.querySelector('.dp-x').addEventListener('click', (e) => { e.stopPropagation(); removeFromTray(m.uid); });
    row.addEventListener('click', () => {
      if (Date.now() - lastDragEnd < 350) return; // a drag just ended on this row, not a tap
      state.pickOpen = state.pickOpen === m.uid ? null : m.uid;
      renderTray();
    });
    const grab = row.querySelector('.dp-grab');
    grab.addEventListener('pointerdown', (ev) => startDrag(ev, m));
    grab.addEventListener('click', (ev) => ev.stopPropagation());
  });
  host.querySelectorAll('.dp-daypick').forEach((pick) => {
    const m = state.tray.find((x) => x.uid === pick.dataset.for);
    pick.querySelectorAll('.dp-dc').forEach((b) => b.addEventListener('click', () => {
      state.pickOpen = null;
      placeInDay(m, b.dataset.day);
    }));
  });
}

// The counter: her numbers are always on it, and it previews the total while a
// dragged meal hovers over the day (like the app's planner adding up as you drag).
function renderDayHead(day, previewMeal) {
  const card = document.querySelector(`.dp-day[data-day="${day.id}"]`);
  if (!card) return;
  const tot = card.querySelector('.dp-tot');
  const t = targets();
  const base = dayTotals(day.id);
  const s = previewMeal ? { cal: base.cal + previewMeal.cal, p: base.p + previewMeal.p } : base;
  const p = r1(s.p);
  card.classList.toggle('dp-preview', !!previewMeal);
  tot.innerHTML = `
    <div class="dp-line"><strong>${s.cal.toLocaleString()}</strong> / ${t.cal.toLocaleString()} cal · <strong>${p}g</strong> / ${t.p}g protein${p >= t.p ? '<span class="dp-pdot" title="protein target hit"></span>' : ''}</div>
    <div class="dp-bar"><div class="fill${s.cal > t.cal ? ' over' : ''}${previewMeal ? ' preview' : ''}" style="width:${Math.min(100, (s.cal / t.cal) * 100)}%"></div></div>`;
}

function renderDayCard(day) {
  const card = document.querySelector(`.dp-day[data-day="${day.id}"]`);
  if (!card) return;
  const body = card.querySelector('.dp-body');
  const t = targets();
  const rows = state.days[day.id];
  renderDayHead(day, null);
  if (rows.length === 0) {
    body.innerHTML = `<div class="dp-empty dp-drop">drag a meal here</div>`;
    return;
  }
  const s = dayTotals(day.id);
  const p = r1(s.p);
  const sorted = SLOT_ORDER.flatMap((slot) => rows.filter((m) => m.slot === slot));
  const left = t.cal - s.cal;
  const pLeft = r1(t.p - p);
  let line;
  if (left >= 0 && pLeft > 0) line = `leaves <strong>${left.toLocaleString()} cal</strong> and <strong>${pLeft}g</strong> protein for the rest of the day.`;
  else if (left >= 0) line = `protein target hit. leaves <strong>${left.toLocaleString()} cal</strong> for the rest of the day.`;
  else line = `<strong>${Math.abs(left).toLocaleString()} cal</strong> over your number. flip a swap or drop a snack.`;
  body.innerHTML = sorted.map((m) => `
    <div class="dp-row" data-uid="${m.uid}">${mealRowHtml(m, '<button type="button" class="dp-x" aria-label="remove">✕</button>')}</div>`).join('')
    + `<div class="dp-left">${line}</div>`;
  body.querySelectorAll('.dp-x').forEach((x) => {
    x.addEventListener('click', () => removeFromDay(day.id, x.closest('.dp-row').dataset.uid));
  });
}

/* ---- drag a tray meal into a day ---- */



/* Ported from the app's food-plan-days.js startDrag (2026-09-02 fixes included):
   the handle owns the gesture (touch-action none there only), the ghost is a
   clone of the row that keeps the grab point under her finger, clamped
   on-screen, and edge auto-scroll runs on a frame timer so a finger held
   still at the edge keeps scrolling while the day highlight recomputes. */
function startDrag(ev, meal) {
  ev.preventDefault();
  ev.stopPropagation();
  const handle = ev.currentTarget;
  const row = handle.closest('.dp-tray-row');
  if (!row) return;
  const ghost = row.cloneNode(true);
  ghost.classList.add('dp-ghost');
  ghost.querySelectorAll('.dp-x, .dp-grab').forEach((x) => x.remove());
  const gw = row.offsetWidth;
  ghost.style.width = gw + 'px';
  document.body.appendChild(ghost);
  row.classList.add('dp-lifted');
  document.body.classList.add('is-dragging');
  state.dragging = true;
  updateDaybar();

  const r0 = row.getBoundingClientRect();
  const offX = ev.clientX - r0.left, offY = ev.clientY - r0.top;
  const place = (e) => {
    const x = Math.max(6, Math.min(e.clientX - offX, window.innerWidth - gw - 6));
    ghost.style.left = x + 'px';
    ghost.style.top = (e.clientY - offY) + 'px';
  };
  let over = null;
  const hover = (e) => {
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const card = el ? el.closest('.dp-day[data-day]') : null;
    const id = card ? card.dataset.day : null;
    if (id !== over) {
      document.querySelectorAll('.dp-day.dp-over').forEach((x) => {
        x.classList.remove('dp-over');
        const d = DAYS.find((y) => y.id === x.dataset.day);
        if (d) renderDayHead(d, null);
      });
      if (card) {
        card.classList.add('dp-over');
        const d = DAYS.find((y) => y.id === id);
        if (d) renderDayHead(d, meal); // the counter adds this meal up while she hovers
      }
      over = id;
    }
  };
  const scroller = document.scrollingElement || document.documentElement;
  let lastE = ev, edge = 0;
  const move = (e) => {
    lastE = e;
    place(e);
    hover(e);
    edge = (e.clientY < 80) ? -1 : (e.clientY > window.innerHeight - 80) ? 1 : 0;
    if (edge) scroller.scrollTop += edge * 9;
  };
  let rafId = 0;
  const tick = () => {
    if (edge) { scroller.scrollTop += edge * 9; hover(lastE); }
    rafId = requestAnimationFrame(tick);
  };
  rafId = requestAnimationFrame(tick);
  const end = () => {
    cancelAnimationFrame(rafId);
    handle.removeEventListener('pointermove', move);
    handle.removeEventListener('pointerup', end);
    handle.removeEventListener('pointercancel', end);
    ghost.remove();
    row.classList.remove('dp-lifted');
    document.body.classList.remove('is-dragging');
    document.querySelectorAll('.dp-day.dp-over').forEach((x) => x.classList.remove('dp-over'));
    state.dragging = false;
    lastDragEnd = Date.now();
    const target = over;
    DAYS.forEach((d) => renderDayHead(d, null));
    updateDaybar();
    if (target) placeInDay(meal, target);
  };
  place(ev);
  try { handle.setPointerCapture(ev.pointerId); } catch (_) { /* fine */ }
  handle.addEventListener('pointermove', move);
  handle.addEventListener('pointerup', end);
  handle.addEventListener('pointercancel', end);
}

/* -------------------------------------------------------------
   STICKY DAY BAR
   Visible on the results page once the cards section has been
   reached, hidden while the day card itself is on screen.
   ------------------------------------------------------------- */
function updateDaybar() {
  const bar = document.getElementById('daybar');
  if (!bar) return;
  const onResults = state.current === 'screen-results';
  const cards = document.getElementById('r-cards');
  const plan = document.getElementById('r-plan');
  if (!onResults || !cards || !plan || state.dragging) { bar.hidden = true; return; }

  const vh = window.innerHeight;
  const cardsReached = cards.getBoundingClientRect().top < vh * 0.7;
  // The bar's job is a running count + a jump while she is up in the cards.
  // Once the plan section scrolls into view it retires so it never sits over
  // the tray, the days, the action card or the CTA button.
  const planBelowViewport = plan.getBoundingClientRect().top > vh - 40;
  bar.hidden = !(cardsReached && planBelowViewport);

  const line = document.getElementById('daybar-line');
  const n = state.tray.length;
  line.innerHTML = n === 0 ? 'nothing added yet' : `<strong>${n} recipe${n === 1 ? '' : 's'}</strong> waiting in your plan`;
}

function wireDaybar() {
  document.getElementById('daybar-go')?.addEventListener('click', () => {
    document.getElementById('r-plan')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => { updateDaybar(); ticking = false; });
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
}

let toastTimer = null;
function toast(msg) {
  const el = document.getElementById('toast');
  if (!el) return;
  el.textContent = msg;
  el.classList.add('is-on');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('is-on'), 1400);
}

/* -------------------------------------------------------------
   RESULTS ACTIONS (restart)
   ------------------------------------------------------------- */
function resetAll() {
  state.bio = { goal: null, activity: null, weight: null, height: null, age: null };
  state.email = null;
  state.macros = null;
  state.guardrails = [];
  state.tray = [];
  state.days = { mon: [], tue: [] };
  state.pickOpen = null;
  state.cardState = {};
  document.querySelectorAll('.choice').forEach((c) => c.classList.remove('is-selected'));
  document.querySelectorAll('.numfield input').forEach((i) => i.value = '');
  document.querySelectorAll('[data-next]').forEach((b) => b.disabled = true);
  const emailEl = document.getElementById('email-input');
  if (emailEl) emailEl.value = '';
  state.history = ['screen-landing'];
  goTo('screen-landing', { pushHistory: false });
}

function wireResultsActions() {
  document.getElementById('action-restart')?.addEventListener('click', () => {
    if (!confirm('start from the top? your answers will reset.')) return;
    resetAll();
  });
}

/* -------------------------------------------------------------
   MARQUEE
   ------------------------------------------------------------- */
function buildMarquee() {
  const track = document.getElementById('marquee-track');
  if (!track) return;
  const html = MARQUEE_PHRASES
    .map((p) => `<span>${p}</span><span class="pip"></span>`)
    .join('');
  // Duplicate for seamless scroll
  track.innerHTML = html + html;
}

/* -------------------------------------------------------------
   INIT
   ------------------------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  wireLandingCTA();
  wireChoiceGroup('screen-goal');
  wireChoiceGroup('screen-activity');
  wireNumberField('screen-weight');
  wireNumberField('screen-height');
  wireNumberField('screen-age');
  wireBackButtons();
  wireEmailGate();
  wireResultsActions();
  wireSlotTabs();
  wireDaybar();
  buildMarquee();

  // Wordmark click — full reset to landing
  document.getElementById('wordmark')?.addEventListener('click', (e) => {
    e.preventDefault();
    resetAll();
  });

  // Dev shortcut — press "d" on landing to skip to results with sample data
  document.addEventListener('keydown', (e) => {
    if (e.key === 'd' && state.current === 'screen-landing' && !e.target.matches('input, textarea')) {
      state.bio = { goal: 'fat-loss', activity: 1.5, weight: 72.5, height: 167, age: 34 };
      state.email = 'preview@mindbodybritt.com';
      computeAndRenderResults();
    }
  });
});
